import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder } from '@angular/forms';
import { buildProviders } from '@kalidea/kaligraphi';
import { tap } from 'rxjs/operators';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import { KalTableComponent } from 'src/app/_shared/modules/kal-table/kal-table.component';
import {
    AdvancedRuleService,
    DataTypes,
    ParameterModel,
    TypesOperateur
} from 'src/app/_shared/services/advanced-rule/advanced-rule.service';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { MoteurCritereDto, MoteurOperateurDto } from 'src/graphql/generated-types';
import { Observable, of } from 'rxjs';

@Component({
    selector: 'app-shared-advanced-rule-criterion-parameters-control',
    templateUrl: './shared-advanced-rule-criterion-parameters-control.component.html',
    styleUrls: ['./shared-advanced-rule-criterion-parameters-control.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedAdvancedRuleCriterionParametersControlComponent),
    standalone: false
})
export class SharedAdvancedRuleCriterionParametersControlComponent extends CustomFormControlAccess implements OnInit {

    paramCriteriaList: MoteurCritereDto[] = [];

    operatorList = this.advancedRuleService.operatorList.filter(({TypeOperateur}) => TypeOperateur === TypesOperateur.Comparaison);
    operatorListForList = this.operatorList.filter(({Libelle}) => ['=', '<>'].includes(Libelle));

    columns: KalTableHeader<ParameterModel>[] = [
        {code: 'IdCritere', label: 'Libellé paramètre'},
        {code: 'IdOperateur', label: 'Opérateur'},
        {code: 'Valeur', label: 'Valeur'},
    ];

    formArray: UntypedFormArray;

    @ViewChild(KalTableComponent) private tableComponent: KalTableComponent<AbstractControl>;

    constructor(private readonly fb: UntypedFormBuilder,
                private readonly advancedRuleService: AdvancedRuleService) {
        super();
    }

    @Input()
    set criterionId(value: number) {
        this.paramCriteriaList = this.advancedRuleService.ruleParamList.filter(({Id}) => value === Id ).map(({IdCritere}) => this.advancedRuleService.criteriaList.find(({Id}) => Id === IdCritere));
    }

    getOperatorList(criterionId: number): MoteurOperateurDto[] {
        return this.paramCriteriaList.find(({Id}) => criterionId === Id)?.TypeDonnee?.Code === DataTypes.List
            ? this.operatorListForList
            : this.operatorList;
    }

    validate(c: AbstractControl): Observable<any> {
        return of(this.formArray.valid ? null : {criterionerror: true});
    }

    writeValue(value: ParameterModel[]): void {
        this.formArray.clear({emitEvent: false});

        if (arrayNotEmpty(value)) {
            value?.forEach(p => this.addParameterLine(p));
        }

        this.tableComponent?.render();
        super.writeValue(value);
    }

    addParameterLine(parameter: ParameterModel = null): void {
        const line = this.fb.group({
            IdCritere: [parameter?.IdCritere || null, [FormValidator.isNotNull]],
            IdOperateur: [parameter?.IdOperateur || null, [FormValidator.isNotNull]],
            Valeur: [parameter?.Valeur || parameter?.IdChamp || null, [FormValidator.isNotNull]],
        });

        this.formArray.push(line, {emitEvent: false});
        if (!parameter) {
            this.tableComponent?.render();
        }
    }

    removeLine(index: number): void {
        this.formArray.removeAt(index);
        this.tableComponent?.render();
    }

    stopPropagation($event: MouseEvent): void {
        $event.stopPropagation();
    }

    private normalize(value: ParameterModel[]): ParameterModel[] {
        return value.map(p => {
            const isList = this.paramCriteriaList.find(({Id}) => p.IdCritere === Id)?.TypeDonnee?.Code === DataTypes.List;
            return {
                ...p,
                Valeur: isList ? null : p.Valeur,
                IdChamp: isList ? +p.Valeur : null
            };
        });
    }

    ngOnInit(): void {
        this.formArray = this.fb.array([]);

        this.watch$ = this.formArray.valueChanges.pipe(
            tap(value => this.notifyUpdate(this.normalize(value)))
        );
    }

}
